import {useTranslation} from 'react-i18next';
import {Mail, MessageCircle, MessageSquareText, Share} from 'lucide-react';
import {ActionRow, CopyAction, cn} from '@chekinapp/ui';
import {
  canShareBySMS,
  canShareNative,
  shareByEmail,
  shareByNative,
  shareBySMS,
  shareByWhatsapp,
} from './utils';

export type ShareRegistrationLinkProps = {
  housingName: string;
  link: string;
};

function ShareRegistrationLink({housingName, link}: ShareRegistrationLinkProps) {
  const {t} = useTranslation();

  const channels = [
    {
      key: 'whatsapp',
      icon: <MessageCircle />,
      title: t('whatsapp'),
      description: t('share_link_whatsapp_description'),
      onClick: () => shareByWhatsapp(housingName, link),
    },
    ...(canShareBySMS()
      ? [
          {
            key: 'sms',
            icon: <MessageSquareText />,
            title: t('sms'),
            description: t('share_link_sms_description'),
            onClick: () => shareBySMS(housingName, link),
          },
        ]
      : []),
    {
      key: 'email',
      icon: <Mail />,
      title: t('email'),
      description: t('share_link_email_description'),
      onClick: () => shareByEmail(housingName, link),
    },
    ...(canShareNative()
      ? [
          {
            key: 'native',
            icon: <Share />,
            title: t('share_link_other_apps'),
            description: t('share_link_other_apps_description'),
            onClick: () => shareByNative(housingName, link),
          },
        ]
      : []),
  ];

  return (
    <div className="share-link-modal w-full">
      <div className="share-link-modal__title pr-8 text-left text-[18px] font-bold leading-6 text-[var(--chekin-color-brand-navy)]">
        {t('share_link_modal_title')}
      </div>
      <p className="mt-1.5 text-left text-[14px] leading-snug text-[var(--chekin-text-secondary)]">
        {t('share_link_modal_hint')}
      </p>

      <div
        className={cn(
          'share-link-modal__link mt-4 flex h-[46px] w-full items-center gap-2.5',
          'rounded-xl bg-[var(--chekin-neutral-75)]',
          'py-0 pl-3.5 pr-1.5',
        )}
      >
        <span className="min-w-0 flex-1 truncate text-left font-mono text-[12px] text-[var(--chekin-text-secondary)]">
          {link}
        </span>
        <CopyAction
          value={link}
          variant="primary"
          className="h-[34px] rounded-[9px] px-3.5 text-[12px]"
        />
      </div>

      <div className="mt-4 flex flex-col gap-2">
        {channels.map(channel => (
          <ActionRow
            key={channel.key}
            icon={channel.icon}
            title={channel.title}
            description={channel.description}
            onClick={channel.onClick}
          />
        ))}
      </div>
    </div>
  );
}

export {ShareRegistrationLink};
